import { Suspense, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { Grid, Skeleton, Stack, Typography } from "@mui/material"
import { DeviceDetailsDialog } from "../../../../entities/devices/components/DeviceDetailsDialog"
import { QueryErrorBoundary } from "../../../../../core/api/components/QueryErrorBoundary"
import {
    DeviceReportAlertAccordion,
    getSeverityIcon,
} from "../../../../../shared/components/alert/DeviceReportAlertAccordion"
import { MerchantHomeQuery, fetchDeviceReport } from "../merchantHomeApi"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { DeviceTypeReport } from "../merchantHomeTypes"
import { CardGroup } from "./CardGroup"

const useStyles = makeStyles()(() => ({
    link: {
        cursor: "pointer",
        transition: "all 0.3s ease-out",
        "&:hover": {
            textDecoration: "underline",
        },
    },
}))

const DefaultDialogState = {
    showDetailsModal: false,
    deviceId: null,
}

export function DeviceReportContent() {
    const { merchantId } = useMerchant()
    const { classes } = useStyles()

    const [dialogDetailsState, setDialogDetailsState] = useState<{
        showDetailsModal: boolean
        deviceId: null | number
    }>(DefaultDialogState)

    const { data: report } = useApiQuery(fetchDeviceReport, {
        queryName: MerchantHomeQuery.DEVICE_REPORT,
        dependencies: {
            merchantId,
        },
        suspense: true,
        useErrorBoundary: true,
        refetchInterval: 30000,
    })

    const handleShowDialog = (deviceId: number) => {
        setDialogDetailsState({ showDetailsModal: true, deviceId: deviceId })
    }

    const renderDeviceList = (devices: DeviceTypeReport[]) => (
        <Grid container direction="column" gap={2}>
            {devices.map((device) => (
                <Stack flexDirection="row" alignItems="center" gap={1} key={device.id}>
                    {getSeverityIcon(device.status.state)}
                    <Typography onClick={() => handleShowDialog(device.id)} className={classes.link}>
                        {device.status?.message ?? ""}
                    </Typography>
                </Stack>
            ))}
        </Grid>
    )

    if (!report || !report?.deviceReport) return null

    return (
        <CardGroup>
            <DeviceReportAlertAccordion
                status={report.deviceReport.state}
                title={report.deviceReport.title}
                message={report.deviceReport.message}
            >
                {renderDeviceList(report.devices)}
            </DeviceReportAlertAccordion>
            {dialogDetailsState.showDetailsModal && dialogDetailsState.deviceId !== null && (
                <DeviceDetailsDialog
                    deviceId={dialogDetailsState.deviceId}
                    open={dialogDetailsState.showDetailsModal}
                    onClose={() => setDialogDetailsState(DefaultDialogState)}
                />
            )}
        </CardGroup>
    )
}

export function DeviceReport() {
    return (
        <QueryErrorBoundary>
            <Suspense
                fallback={
                    <>
                        <Skeleton variant="rectangular" animation="wave" width="100%" height="4.5rem" />
                    </>
                }
            >
                <DeviceReportContent />
            </Suspense>
        </QueryErrorBoundary>
    )
}
